/**
 * @fileoverview Full-page user creation for tablet and mobile.
 * @module components/UserCreationView/index
 */

import { SvgIcon } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import LanguageSelector from "../LanguageSelector";
import NotificationBell from "../NotificationBell/index.jsx";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";
import styles from "./UserCreationView.module.css";
import { useUserCreationForm } from "../userCreation/useUserCreationForm";
import {
  UserCreationAvatar,
  UserCreationContactFields,
  UserCreationIdentityFields,
} from "../userCreation/UserCreationFields";
import {
  CREATE_BUTTON_SPINNER_SIZE,
  VIEW_SELECT_LAYOUT,
  userTypeSelectStyles,
} from "../userCreation/userCreationFormModel";

const I18N_PREFIX = "components.userCreationView";

const fieldClasses = {
  section: styles.formSection,
  label: styles.formLabel,
  input: styles.formInput,
  inputContainer: styles.inputContainer,
  inputWrapper: styles.inputWrapper,
  inputIcon: styles.inputIcon,
  inputClearButton: styles.inputClearButton,
  divider: styles.divider,
};

/**
 * Full-page view for creating a user. Company members can type an RFC.
 * Other accounts keep the company RFC already on the account.
 * @param {Object} props
 * @param {Function} props.onBack
 * @param {Function} props.onAddUser
 * @param {Object} [props.user]
 * @returns {JSX.Element}
 */
const UserCreationView = ({ onBack, onAddUser, user }) => {
  const form = useUserCreationForm({
    user,
    active: true,
    onAddUser,
    onFinished: onBack,
  });

  return (
    <>
      <div className={styles.userCreationContainer}>
        <div className={styles.header}>
          <div className={styles.languages}>
            <NotificationBell navigate={form.navigate} />
            <LanguageSelector />
          </div>
          <div className={styles.backNavigation}>
            <button className={styles.backButton} onClick={onBack}>
              <ChevronLeftIcon />
              <span>{form.t("common.goBack")}</span>
            </button>
          </div>
          <div className={styles.headerInfoContainer}>
            <div className={styles.headerInfo}>
              <SvgIcon sx={{ fontSize: "32px" }} viewBox="0 0 24 24">
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#4eabe7" />
                    <stop offset="100%" stopColor="#2f59df" />
                  </linearGradient>
                </defs>
                <PersonAddOutlinedIcon sx={{ fill: "url(#gradient1)" }} />
              </SvgIcon>
              <h1 className={styles.title}>{form.t(`${I18N_PREFIX}.createUser`)}</h1>
            </div>
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.avatarSection}>
            <div className={styles.avatarContainer}>
              <UserCreationAvatar
                form={form}
                alt={form.t(`${I18N_PREFIX}.avatarAlt`)}
                classes={{
                  avatar: styles.avatar,
                  avatarImage: styles.avatarImage,
                  avatarButtons: styles.avatarButtons,
                  editButton: styles.avatarEditButton,
                  deleteButton: styles.avatarDeleteButton,
                }}
              />
            </div>
          </div>
          <div className={styles.formContent}>
            <UserCreationIdentityFields
              form={form}
              classes={fieldClasses}
              i18nPrefix={I18N_PREFIX}
              selectStyles={userTypeSelectStyles(form.userTypeLocked, VIEW_SELECT_LAYOUT)}
            />
            <UserCreationContactFields
              form={form}
              user={user}
              classes={fieldClasses}
              i18nPrefix={I18N_PREFIX}
            />
            {form.createError && (
              <p className={styles.formError} role="alert">
                {form.createError}
              </p>
            )}
            <div className={styles.actionButtons}>
              <button onClick={onBack} className={styles.cancelButton}>
                {form.t(`${I18N_PREFIX}.cancel`)}
              </button>
              <button
                className={styles.createButton}
                onClick={form.handleCreateUser}
                disabled={form.loading || !form.canSubmit}
              >
                {form.loading ? (
                  <CircularProgress size={CREATE_BUTTON_SPINNER_SIZE} />
                ) : (
                  form.t(`${I18N_PREFIX}.createUser`)
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {form.showSuccessMessage && (
        <div className={styles.successMessage}>
          {form.t(`${I18N_PREFIX}.successMessage`)}
        </div>
      )}
    </>
  );
};

export default UserCreationView;
